import { useState } from "react";

export default function TesteTecnico() {
  const [activeSection, setActiveSection] = useState<
    "challenge" | "api" | "requirements"
  >("challenge");

  const navigationItems = [
    { id: "challenge" as const, label: "Desafio", icon: "🎯" },
    { id: "api" as const, label: "API", icon: "🔗" },
    { id: "requirements" as const, label: "Requisitos", icon: "📋" },
  ];

  const techStack = [
    {
      name: "React",
      color: "bg-blue-500",
      description: "Para criar a interface",
    },
    {
      name: "TypeScript",
      color: "bg-blue-600",
      description: "Para tipagem estática",
    },
    {
      name: "Tailwind CSS",
      color: "bg-cyan-500",
      description: "Para estilização",
    },
    { name: "JWT", color: "bg-purple-500", description: "Para autenticação" },
    {
      name: "REST API",
      color: "bg-green-500",
      description: "Para comunicação",
    },
  ];

  const features = [
    {
      title: "Autenticação",
      description: "Sistema de login e registro de usuários",
      icon: "🔐",
      tasks: [
        "Formulário de login",
        "Formulário de registro",
        "Gerenciamento de token JWT",
        "Proteção de rotas",
      ],
    },
    {
      title: "CRUD de Clientes",
      description: "Operações completas de gerenciamento",
      icon: "👥",
      tasks: [
        "Listar clientes",
        "Criar novo cliente",
        "Editar cliente existente",
        "Excluir cliente",
      ],
    },
    {
      title: "Interface Responsiva",
      description: "Design adaptável e moderno",
      icon: "📱",
      tasks: [
        "Layout responsivo",
        "Componentes reutilizáveis",
        "Feedback visual",
        "Estados de loading",
      ],
    },
    {
      title: "Tratamento de Erros",
      description: "Experiência consistente para o usuário",
      icon: "⚠️",
      tasks: [
        "Validação de formulários",
        "Mensagens de erro",
        "Estados de erro da API",
        "Fallbacks visuais",
      ],
    },
  ];

  const apiEndpoints = [
    {
      method: "POST",
      endpoint: "/auth/register",
      description: "Registrar novo usuário",
      color: "bg-green-100 text-green-800",
    },
    {
      method: "POST",
      endpoint: "/auth/login",
      description: "Fazer login",
      color: "bg-green-100 text-green-800",
    },
    {
      method: "GET",
      endpoint: "/clients",
      description: "Listar clientes",
      color: "bg-blue-100 text-blue-800",
    },
    {
      method: "POST",
      endpoint: "/clients",
      description: "Criar cliente",
      color: "bg-green-100 text-green-800",
    },
    {
      method: "PUT",
      endpoint: "/clients/:id",
      description: "Atualizar cliente",
      color: "bg-yellow-100 text-yellow-800",
    },
    {
      method: "DELETE",
      endpoint: "/clients/:id",
      description: "Deletar cliente",
      color: "bg-red-100 text-red-800",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-azul-400/20 to-azul-300">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-azul-400/20">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-cinza-800 mb-4">
              🚀 Desafio Técnico - Sistema de Gerenciamento de Clientes
            </h1>
            <p className="text-xl text-cinza-500 max-w-3xl mx-auto">
              Desenvolva uma aplicação React completa integrando com nossa API
              REST para gerenciar clientes
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white shadow-sm border-b border-azul-400/20">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex space-x-8">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`py-4 px-2 relative font-medium text-sm transition-colors duration-200 ${
                  activeSection === item.id
                    ? "text-azul-400 border-b-2 border-azul-400"
                    : "text-cinza-500 hover:text-azul-400"
                }`}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {activeSection === "challenge" && (
          <div className="space-y-12">
            {/* Hero Section */}
            <div className="text-center bg-white rounded-xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-cinza-800 mb-6">
                💡 Seu Desafio
              </h2>
              <p className="text-lg text-cinza-600 max-w-4xl mx-auto leading-relaxed">
                Desenvolva uma aplicação React moderna que permita aos usuários
                gerenciar seus clientes de forma intuitiva e segura. A aplicação
                deve consumir nossa API REST e oferecer uma experiência completa
                de CRUD (Create, Read, Update, Delete).
              </p>
            </div>

            {/* Tech Stack */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-cinza-800 mb-6 text-center">
                🛠️ Stack Tecnológica
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {techStack.map((tech, index) => (
                  <div
                    key={index}
                    className="text-center p-4 rounded-lg border border-cinza-100 hover:shadow-md transition-shadow"
                  >
                    <div
                      className={`${tech.color} text-white p-3 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center font-bold`}
                    >
                      {tech.name.charAt(0)}
                    </div>
                    <h4 className="font-semibold text-cinza-800 mb-1">
                      {tech.name}
                    </h4>
                    <p className="text-sm text-cinza-500">{tech.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-cinza-800 mb-8 text-center">
                ✨ Funcionalidades a Implementar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="border border-cinza-100 rounded-lg p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center mb-4">
                      <span className="text-3xl mr-4">{feature.icon}</span>
                      <div>
                        <h4 className="text-xl font-semibold text-cinza-800">
                          {feature.title}
                        </h4>
                        <p className="text-cinza-600">{feature.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {feature.tasks.map((task, taskIndex) => (
                        <li
                          key={taskIndex}
                          className="flex items-center text-sm text-cinza-600"
                        >
                          <span className="w-2 h-2 bg-azul-400 rounded-full mr-3"></span>
                          {task}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-azul-400 to-azul-300 rounded-xl p-8 text-center text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-4">
                🎯 Pronto para o Desafio?
              </h3>
              <p className="text-lg mb-6 opacity-90">
                Explore a documentação da API e os requisitos técnicos para
                começar seu desenvolvimento!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setActiveSection("api")}
                  className="bg-white text-azul-400 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  📖 Ver Documentação da API
                </button>
                <button
                  onClick={() => setActiveSection("requirements")}
                  className="bg-azul-300 text-white px-6 py-3 rounded-lg font-semibold hover:bg-azul-200 transition-colors"
                >
                  📋 Ver Requisitos Técnicos
                </button>
              </div>
            </div>
          </div>
        )}

        {activeSection === "api" && (
          <div className="space-y-8">
            {/* API Info */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-cinza-800 mb-6 text-center">
                🔗 Documentação da API
              </h2>

              {/* Base URL */}
              <div className="bg-cinza-100 rounded-lg p-4 mb-8">
                <h3 className="text-lg font-semibold text-cinza-800 mb-2">
                  Base URL:
                </h3>
                <code className="text-azul-400 font-mono bg-white px-3 py-2 rounded">
                  https://goon-teste-api-production.up.railway.app/api
                </code>
              </div>

              {/* Endpoints */}
              <h3 className="text-xl font-semibold text-cinza-800 mb-6">
                🛠️ Endpoints Disponíveis
              </h3>
              <div className="space-y-4">
                {apiEndpoints.map((endpoint, index) => (
                  <div
                    key={index}
                    className="border border-cinza-100 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${endpoint.color} w-fit`}
                      >
                        {endpoint.method}
                      </span>
                      <code className="font-mono text-cinza-800 bg-cinza-100 px-3 py-1 rounded flex-1">
                        {endpoint.endpoint}
                      </code>
                      <span className="text-cinza-600 text-sm">
                        {endpoint.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Authentication */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-cinza-800 mb-6">
                🔐 Autenticação
              </h3>
              <div className="space-y-4">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <p className="text-yellow-800">
                    <strong>Importante:</strong> Todas as rotas de clientes
                    requerem autenticação JWT.
                  </p>
                </div>
                <div className="bg-cinza-100 rounded-lg p-4">
                  <h4 className="font-semibold text-cinza-800 mb-2">
                    Header de Autorização:
                  </h4>
                  <code className="text-azul-400 font-mono bg-white px-3 py-2 rounded block">
                    Authorization: Bearer seu_token_aqui
                  </code>
                </div>
              </div>
            </div>

            {/* Example Request */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-cinza-800 mb-6">
                📝 Exemplos de Requisição
              </h3>
              {/* Exemplo: Registro */}
              <div className="mb-6">
                <div className="text-gray-400 mb-2 font-mono">
                  // Registrar novo usuário
                </div>
                <div className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-sm overflow-x-auto">
                  <div className="text-blue-400">const</div>{" "}
                  <span className="text-yellow-400">response</span> ={" "}
                  <span className="text-blue-400">await</span>{" "}
                  <span className="text-yellow-400">fetch</span>(
                  <span className="text-green-300">
                    'https://goon-teste-api-production.up.railway.app/api/auth/register'
                  </span>
                  , {"{"}
                  <br />
                  &nbsp;&nbsp;<span className="text-red-400">method</span>:{" "}
                  <span className="text-green-300">'POST'</span>,<br />
                  &nbsp;&nbsp;<span className="text-red-400">
                    headers
                  </span>: {"{"}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-green-300">'Content-Type'</span>:{" "}
                  <span className="text-green-300">'application/json'</span>,
                  <br />
                  &nbsp;&nbsp;{"}"},<br />
                  &nbsp;&nbsp;<span className="text-red-400">body</span>:{" "}
                  <span className="text-yellow-400">JSON</span>.
                  <span className="text-yellow-400">stringify</span>({"{"}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-red-400">name</span>:{" "}
                  <span className="text-green-300">'João Silva'</span>,<br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-red-400">email</span>:{" "}
                  <span className="text-green-300">'joao@email.com'</span>,
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-red-400">password</span>:{" "}
                  <span className="text-green-300">'senha123'</span>
                  <br />
                  &nbsp;&nbsp;{""})<br />
                  {""});
                </div>
              </div>

              {/* Exemplo: Login */}
              <div className="mb-6">
                <div className="text-gray-400 mb-2 font-mono">
                  // Login do usuário
                </div>
                <div className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-sm overflow-x-auto">
                  <div className="text-blue-400">const</div>{" "}
                  <span className="text-yellow-400">response</span> ={" "}
                  <span className="text-blue-400">await</span>{" "}
                  <span className="text-yellow-400">fetch</span>(
                  <span className="text-green-300">
                    'https://goon-teste-api-production.up.railway.app/api/auth/login'
                  </span>
                  , {"{"}
                  <br />
                  &nbsp;&nbsp;<span className="text-red-400">method</span>:{" "}
                  <span className="text-green-300">'POST'</span>,<br />
                  &nbsp;&nbsp;<span className="text-red-400">
                    headers
                  </span>: {"{"}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-green-300">'Content-Type'</span>:{" "}
                  <span className="text-green-300">'application/json'</span>,
                  <br />
                  &nbsp;&nbsp;{"}"},<br />
                  &nbsp;&nbsp;<span className="text-red-400">body</span>:{" "}
                  <span className="text-yellow-400">JSON</span>.
                  <span className="text-yellow-400">stringify</span>({"{"}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-red-400">email</span>:{" "}
                  <span className="text-green-300">'joao@email.com'</span>,
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-red-400">password</span>:{" "}
                  <span className="text-green-300">'senha123'</span>
                  <br />
                  &nbsp;&nbsp;{""})<br />
                  {""});
                </div>
              </div>

              {/* Exemplo: Criar Cliente */}
              <div>
                <div className="text-gray-400 mb-2 font-mono">
                  // Criar um novo cliente
                </div>
                <div className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-sm overflow-x-auto">
                  <div className="text-blue-400">const</div>{" "}
                  <span className="text-yellow-400">response</span> ={" "}
                  <span className="text-blue-400">await</span>{" "}
                  <span className="text-yellow-400">fetch</span>(
                  <span className="text-green-300">
                    'https://goon-teste-api-production.up.railway.app/api/clients'
                  </span>
                  , {"{"}
                  <br />
                  &nbsp;&nbsp;<span className="text-red-400">method</span>:{" "}
                  <span className="text-green-300">'POST'</span>,<br />
                  &nbsp;&nbsp;<span className="text-red-400">
                    headers
                  </span>: {"{"}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-green-300">'Content-Type'</span>:{" "}
                  <span className="text-green-300">'application/json'</span>,
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-green-300">'Authorization'</span>:{" "}
                  <span className="text-green-300">
                    `Bearer ${"{"}token{"}"}`
                  </span>
                  ,<br />
                  &nbsp;&nbsp;{""},<br />
                  &nbsp;&nbsp;<span className="text-red-400">body</span>:{" "}
                  <span className="text-yellow-400">JSON</span>.
                  <span className="text-yellow-400">stringify</span>({"{"}
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-red-400">name</span>:{" "}
                  <span className="text-green-300">'João Silva'</span>,<br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-red-400">email</span>:{" "}
                  <span className="text-green-300">'joao@email.com'</span>,
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-red-400">phone</span>:{" "}
                  <span className="text-green-300">'11999999999'</span>,<br />
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span className="text-red-400">company</span>:{" "}
                  <span className="text-green-300">'Empresa ABC'</span>
                  <br />
                  &nbsp;&nbsp;{""})<br />
                  {""});
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === "requirements" && (
          <div className="space-y-8">
            {/* Technical Requirements */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-cinza-800 mb-6 text-center">
                📋 Requisitos Técnicos
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Mandatory */}
                <div className="border-l-4 border-green-500 pl-6">
                  <h3 className="text-xl font-semibold text-green-700 mb-4">
                    ✅ Obrigatório
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "React 18+ com TypeScript",
                      "Tailwind CSS para estilização",
                      "Integração completa com a API REST",
                      "Sistema de autenticação JWT",
                      "CRUD completo de clientes",
                      "Formulários com validação",
                      "Tratamento de erros da API",
                      "Interface responsiva",
                      "Código limpo e bem estruturado",
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-green-500 mr-2 mt-1">✓</span>
                        <span className="text-cinza-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Optional */}
                <div className="border-l-4 border-blue-500 pl-6">
                  <h3 className="text-xl font-semibold text-blue-700 mb-4">
                    🌟 Diferencial
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Context API ou Redux para estado global",
                      "React Query para cache de dados",
                      "Testes unitários (Jest/Testing Library)",
                      "Loading states e skeleton screens",
                      "Debounce em campos de busca",
                      "Paginação de resultados",
                      "Toasts para feedback do usuário",
                      "Validação de formulários avançada",
                      "Deploy em Vercel ou Netlify",
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-500 mr-2 mt-1">⭐</span>
                        <span className="text-cinza-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Project Structure */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-cinza-800 mb-6">
                📁 Estrutura Sugerida
              </h3>
              <div className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-sm overflow-x-auto">
                <div>src/</div>
                <div>├── components/</div>
                <div>
                  │ ├── common/{" "}
                  <span className="text-gray-500">
                    # Componentes reutilizáveis
                  </span>
                </div>
                <div>
                  │ ├── forms/{" "}
                  <span className="text-gray-500"># Formulários</span>
                </div>
                <div>
                  │ └── layout/{" "}
                  <span className="text-gray-500"># Layout da aplicação</span>
                </div>
                <div>├── pages/</div>
                <div>
                  │ ├── auth/{" "}
                  <span className="text-gray-500"># Login/Registro</span>
                </div>
                <div>
                  │ └── clients/{" "}
                  <span className="text-gray-500"># Páginas de clientes</span>
                </div>
                <div>├── services/</div>
                <div>
                  │ └── api.ts{" "}
                  <span className="text-gray-500"># Configuração da API</span>
                </div>
                <div>├── hooks/</div>
                <div>
                  │ └── useAuth.ts{" "}
                  <span className="text-gray-500"># Hook de autenticação</span>
                </div>
                <div>├── context/</div>
                <div>
                  │ └── AuthContext.tsx{" "}
                  <span className="text-gray-500">
                    # Context de autenticação
                  </span>
                </div>
                <div>├── types/</div>
                <div>
                  │ └── index.ts{" "}
                  <span className="text-gray-500"># Tipagens TypeScript</span>
                </div>
                <div>└── utils/</div>
                <div>
                  {" "}
                  └── helpers.ts{" "}
                  <span className="text-gray-500"># Funções utilitárias</span>
                </div>
              </div>
            </div>

            {/* Evaluation Criteria */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-cinza-800 mb-6">
                🎯 Critérios de Avaliação
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    title: "Funcionalidade",
                    weight: "40%",
                    items: [
                      "Integração com API",
                      "CRUD completo",
                      "Autenticação",
                      "Validações",
                    ],
                  },
                  {
                    title: "Código",
                    weight: "35%",
                    items: [
                      "Organização",
                      "Reutilização",
                      "TypeScript",
                      "Boas práticas",
                    ],
                  },
                  {
                    title: "UX/UI",
                    weight: "25%",
                    items: [
                      "Design responsivo",
                      "Usabilidade",
                      "Estados de loading",
                      "Feedback visual",
                    ],
                  },
                ].map((criteria, index) => (
                  <div
                    key={index}
                    className="border border-cinza-100 rounded-lg p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-cinza-800">
                        {criteria.title}
                      </h4>
                      <span className="bg-azul-400/20 text-azul-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {criteria.weight}
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {criteria.items.map((item, itemIndex) => (
                        <li
                          key={itemIndex}
                          className="flex items-center text-sm text-cinza-600"
                        >
                          <span className="w-2 h-2 bg-azul-400 rounded-full mr-3"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-gradient-to-r from-azul-400 to-azul-300 rounded-xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-6 text-center">
                ⏰ Prazo e Entrega
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div className="bg-white bg-opacity-20 rounded-lg p-6">
                  <div className="text-3xl mb-2">📅</div>
                  <h4 className="text-lg font-semibold mb-2">Prazo</h4>
                  <p className="opacity-90">
                    3 dias corridos para desenvolvimento completo
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-6">
                  <div className="text-3xl mb-2">📤</div>
                  <h4 className="text-lg font-semibold mb-2">Entrega</h4>
                  <p className="opacity-90">
                    Repositório GitHub com README detalhado
                  </p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-6">
                  <div className="text-3xl mb-2">🚀</div>
                  <h4 className="text-lg font-semibold mb-2">Demo</h4>
                  <p className="opacity-90">
                    Link para aplicação funcionando (opcional)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-cinza-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold mb-4">💡 Dicas para o Sucesso</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
            {[
              {
                icon: "📖",
                title: "Leia toda a documentação",
                desc: "Entenda a API antes de começar",
              },
              {
                icon: "🏗️",
                title: "Comece simples",
                desc: "Implemente o básico primeiro",
              },
              {
                icon: "✅",
                title: "Teste cada funcionalidade",
                desc: "Valide conforme desenvolve",
              },
              {
                icon: "📝",
                title: "Documente bem",
                desc: "README claro com instruções",
              },
            ].map((tip, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-3">{tip.icon}</div>
                <h4 className="font-semibold mb-2">{tip.title}</h4>
                <p className="text-cinza-300 text-sm">{tip.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 pt-8 border-t border-cinza-700">
            <p className="text-cinza-400">
              Boa sorte com o desafio! Estamos ansiosos para ver sua solução. 🚀
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
